import { apiFetch, postMultipart } from "./client";

export type ContractStatus =
  | "DRAFT"
  | "SENT_TO_CUSTOMER"
  | "CUSTOMER_VIEWED"
  | "SIGNED"
  | "ACTIVE"
  | "EXPIRED"
  | "CANCELLED";

export type ContractVersion = {
  id: string;
  versionNo: number;
  filename: string;
  sizeBytes: number;
  contentType: string;
  signed: boolean;
  uploadedBy: string | null;
  uploadedAt: string;
};

export type Contract = {
  id: string;
  companyId: string;
  companyName: string;
  referenceNumber: string;
  title: string;
  status: ContractStatus;
  effectiveDate: string | null;
  expiresAt: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  versionCount: number;
  latestVersion: ContractVersion | null;
};

const q = (params: Record<string, string | undefined>) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => v && usp.set(k, v));
  const s = usp.toString();
  return s ? `?${s}` : "";
};

async function uploadVersionMultipart(contractId: string, file: File, markSigned: boolean) {
  const fd = new FormData();
  fd.append("file", file);
  return postMultipart<ContractVersion>(`/api/contracts/${contractId}/versions?markSigned=${markSigned}`, fd);
}

export const contractsApi = {
  list: (params: { companyId?: string; status?: ContractStatus } = {}) =>
    apiFetch<Contract[]>(`/contracts${q(params)}`),
  get: (id: string) => apiFetch<Contract>(`/contracts/${id}`),
  versions: (id: string) => apiFetch<ContractVersion[]>(`/contracts/${id}/versions`),
  presign: (id: string, versionId: string) =>
    apiFetch<{ url: string; ttlSeconds: string }>(`/contracts/${id}/versions/${versionId}/download-url`),

  create: (body: {
    companyId: string;
    referenceNumber: string;
    title: string;
    effectiveDate?: string;
    expiresAt?: string;
  }) => apiFetch<Contract>("/contracts", { method: "POST", body }),

  update: (id: string, body: Partial<Pick<Contract, "title" | "effectiveDate" | "expiresAt">>) =>
    apiFetch<Contract>(`/contracts/${id}`, { method: "PUT", body }),

  transition: (id: string, to: ContractStatus) =>
    apiFetch<Contract>(`/contracts/${id}/transition`, { method: "POST", body: { to } }),

  uploadVersion: (id: string, file: File, markSigned = false) => uploadVersionMultipart(id, file, markSigned),
};
